import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { resetPassword, AuthError } from '../lib/auth';
import { AuthShell, AuthBanner, Field } from '../components/AuthShell';
import { validatePassword, PASSWORD_HINT } from '../lib/password-policy';

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const pwError = validatePassword(password);
    if (pwError) {
      setError(pwError);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (err) {
      setError(err instanceof AuthError ? err.message : 'Could not reset password. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Reset password" footer={<Link to="/forgot-password">Request a new link</Link>}>
        <AuthBanner kind="error">This reset link is missing its token. Please request a new one.</AuthBanner>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Set a new password"
      footer={
        <span className="muted">
          <Link to="/login">Back to sign in</Link>
        </span>
      }
    >
      {done ? (
        <AuthBanner kind="success">Password updated. Redirecting you to sign in…</AuthBanner>
      ) : (
        <>
          {error && <AuthBanner kind="error">{error}</AuthBanner>}
          <form onSubmit={submit}>
            <Field label="New password" type="password" value={password} onChange={setPassword} autoComplete="new-password" placeholder={PASSWORD_HINT} required autoFocus />
            <Field label="Confirm password" type="password" value={confirm} onChange={setConfirm} autoComplete="new-password" required />
            <button type="submit" className="btn btn--accent" disabled={busy} style={{ width: '100%', height: 38 }}>
              {busy ? 'Updating…' : 'Update password'}
            </button>
          </form>
        </>
      )}
    </AuthShell>
  );
}
